import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import TabIcon from '@mui/icons-material/Tab';
import MailIcon from '@mui/icons-material/Mail';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { Invoice, InvoiceDelivery, InvoiceItemContextEvent, InvoicePaymentStatus } from 'models/invoice';
import { useNavigate } from 'react-router-dom';
import { CONSTANTS } from 'config/constants';
import { permissions } from 'utils/permission';

type InvoiceItemContextMenuProps = {
    invoiceContextEvent: InvoiceItemContextEvent;
    onCloseContextMenu: () => void;
    openPaidDateModal: () => void;
    openSetUnpaidModal: () => void;
    openSetSendDateDialog: () => void;
    openSendInvoiceDialog: (invoice: Invoice) => void;
};
const InvoiceItemContextMenu: FC<InvoiceItemContextMenuProps> = ({
    invoiceContextEvent,
    onCloseContextMenu,
    openSendInvoiceDialog,
    openPaidDateModal,
    openSetUnpaidModal,
    openSetSendDateDialog,
}: InvoiceItemContextMenuProps) => {
    const { t: translate } = useTranslation(['invoices']);
    const navigate = useNavigate();
    const invoiceSummaryUrl = `/invoices/${invoiceContextEvent.invoice._id}/summary`;
    const currentUser = currentUserVar();

    const navigateToInvoiceSummary = (newTab: boolean) => {
        if (newTab) {
            window.open(invoiceSummaryUrl, '_blank');
            return;
        } else {
            navigate(invoiceSummaryUrl);
        }
        onCloseContextMenu();
    };

    return (
        <Fragment>
            <Menu
                open={invoiceContextEvent !== null}
                onClose={onCloseContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    invoiceContextEvent !== null
                        ? { top: invoiceContextEvent.mouseY + 5, left: invoiceContextEvent.mouseX + 5 }
                        : undefined
                }
            >
                <MenuItem
                    onClick={() => {
                        navigateToInvoiceSummary(true);
                    }}
                >
                    <ListItemIcon>
                        <TabIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('invoices:OPEN_INVOICE_IN_NEW_TAB')}</ListItemText>
                </MenuItem>
                {permissions.isLabTechnician(currentUser.type) ? (
                    <Fragment>
                        {invoiceContextEvent.invoice.invoiceSettings?.invoiceDelivery === InvoiceDelivery.PRINT ||
                        invoiceContextEvent.invoice.invoiceSettings?.invoiceDelivery === InvoiceDelivery.EMAIL ? (
                            <MenuItem
                                onClick={() => {
                                    openSendInvoiceDialog(invoiceContextEvent.invoice);
                                }}
                            >
                                <ListItemIcon>
                                    <MailIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{translate('invoices:SEND_INVOICE_BY_EMAIL')}</ListItemText>
                            </MenuItem>
                        ) : undefined}
                        {invoiceContextEvent.invoice.invoiceSettings?.invoiceDelivery === InvoiceDelivery.PRINT ? (
                            <MenuItem
                                onClick={() => {
                                    openSetSendDateDialog();
                                }}
                            >
                                <ListItemIcon>
                                    <EventAvailableIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{translate('invoices:REGISTER_SENT_DATE')}</ListItemText>
                            </MenuItem>
                        ) : undefined}
                        {!invoiceContextEvent.invoice.paymentStatus ||
                        invoiceContextEvent.invoice.paymentStatus === InvoicePaymentStatus.UNPAID ? (
                            <MenuItem
                                onClick={() => {
                                    openPaidDateModal();
                                }}
                            >
                                <ListItemIcon>
                                    <AttachMoneyIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{translate('invoices:SET_PAID')}</ListItemText>
                            </MenuItem>
                        ) : (
                            <MenuItem
                                onClick={() => {
                                    openSetUnpaidModal();
                                }}
                            >
                                <ListItemIcon>
                                    <MoneyOffIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{translate('invoices:SET_UNPAID')}</ListItemText>
                            </MenuItem>
                        )}
                    </Fragment>
                ) : undefined}
            </Menu>
        </Fragment>
    );
};

export default InvoiceItemContextMenu;
